import {
  Duration,
  Instant,
  LocalDate,
  LocalTime,
  ZoneId,
  ZonedDateTime,
} from "@js-joda/core";
import "@js-joda/timezone";
import { err, ok } from "utils/result";
import type { Result } from "utils/result";

// TODO: Replace hardcoded Stockholm timezone with a dynamic per-company timezone from company settings when multi-timezone support is needed

/** Time zone used for schedule display and bucketing. */
const STOCKHOLM_TIME_ZONE = ZoneId.of("Europe/Stockholm");

/** Returns the current date in Stockholm local time. */
export const getStockholmLocalDateNow = (): LocalDate =>
  ZonedDateTime.now(STOCKHOLM_TIME_ZONE).toLocalDate();

/** Parse a UTC ISO string into a Stockholm-zoned date-time. */
const toStockholmZonedDateTime = (value: string): ZonedDateTime | null => {
  try {
    const instant = Instant.parse(value);
    return ZonedDateTime.ofInstant(instant, STOCKHOLM_TIME_ZONE);
  } catch {
    return null;
  }
};

const toStockholmDate = (value: string): Date | null => {
  const zoned = toStockholmZonedDateTime(value);
  if (!zoned) return null;
  return new Date(zoned.toInstant().toEpochMilli());
};

/** Convert a UTC ISO string into a LocalDate in Stockholm time. */
export const toStockholmLocalDateFromUtcIso = (
  value?: string | null,
): LocalDate | null => {
  if (!value) return null;
  const zoned = toStockholmZonedDateTime(value);
  if (!zoned) return null;
  return zoned.toLocalDate();
};

/** Format a LocalDate as YYYYMMDD for API requests. */
export const formatIsoDate = (date: LocalDate): string => {
  const month = String(date.monthValue()).padStart(2, "0");
  const day = String(date.dayOfMonth()).padStart(2, "0");
  return `${date.year()}${month}${day}`;
};

/** Format a LocalDate as YYYY-MM-DD for client-side lookups. */
export const formatDateKey = (date: LocalDate): string =>
  `${date.year()}-${String(date.monthValue()).padStart(2, "0")}-${String(
    date.dayOfMonth(),
  ).padStart(2, "0")}`;

/** Extract a YYYY-MM-DD key from an ISO date/time string in local time. */
export const formatItemDateKey = (value: string): string => {
  const zoned = toStockholmZonedDateTime(value);
  if (!zoned) return value;
  const year = zoned.year();
  const month = toTwoDigits(zoned.monthValue());
  const day = toTwoDigits(zoned.dayOfMonth());
  return `${year}-${month}-${day}`;
};

/** Format an ISO date/time string to a localized "day month year" label (local time). */
export const formatDateLabel = (value: string, locale: string): string => {
  const parsed = toStockholmDate(value);
  if (!parsed) return value;
  return new Intl.DateTimeFormat(locale, {
    day: "numeric",
    month: "short",
    year: "numeric",
    timeZone: STOCKHOLM_TIME_ZONE.id(),
  }).format(parsed);
};

/** Format an ISO date/time string to a localized "day month" label (local time). */
export const formatDayMonthLabel = (value: string, locale: string): string => {
  const parsed = toStockholmDate(value);
  if (!parsed) return value;
  return new Intl.DateTimeFormat(locale, {
    day: "numeric",
    month: "long",
    timeZone: STOCKHOLM_TIME_ZONE.id(),
  }).format(parsed);
};

/** Format an ISO date/time string to a localized time label (local time). */
export const formatTimeLabel = (value: string, locale: string): string => {
  const parsed = toStockholmDate(value);
  if (!parsed) return value;
  return new Intl.DateTimeFormat(locale, {
    hour: "2-digit",
    minute: "2-digit",
    timeZone: STOCKHOLM_TIME_ZONE.id(),
  }).format(parsed);
};

/** Format a start/end time pair into a localized time range. */
export const formatTimeRange = (
  startTime: string,
  endTime: string,
  locale: string,
): string => {
  return `${formatTimeLabel(startTime, locale)} - ${formatTimeLabel(
    endTime,
    locale,
  )}`;
};

/** Format a break range label from break times or break minutes. */
export const formatBreakRangeLabel = (params: {
  breakStartTime?: string | null;
  breakEndTime?: string | null;
  breakTime?: number | null;
  locale: string;
}): string => {
  if (params.breakStartTime && params.breakEndTime) {
    return formatTimeRange(
      params.breakStartTime,
      params.breakEndTime,
      params.locale,
    );
  }

  if (params.breakTime != null) return `${params.breakTime}m`;
  return "--";
};

/** Check if a shift end time is in the past. */
export const hasShiftPassed = (endTime: string): boolean => {
  try {
    const end = Instant.parse(endTime);
    return end.isBefore(Instant.now());
  } catch {
    return false;
  }
};

/** Format total time between start/end minus break minutes. */
export const formatTotalTime = (
  startTime: string,
  endTime: string,
  breakMinutes: number,
): string | null => {
  let totalMinutes = 0;
  try {
    const start = Instant.parse(startTime);
    const end = Instant.parse(endTime);
    totalMinutes = Math.max(
      0,
      Duration.between(start, end).toMinutes() - breakMinutes,
    );
  } catch {
    return null;
  }
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  if (hours <= 0) return `${minutes}m`;
  if (minutes === 0) return `${hours}h`;
  return `${hours}h ${minutes}m`;
};

/** Convert a YYYY-MM-DD date string into a Date at Stockholm midnight. */
export const toStockholmDateFromDateOnly = (
  value: string,
): Result<Date, "invalid-date"> => {
  try {
    const localDate = LocalDate.parse(value);
    const zoned = ZonedDateTime.of(
      localDate,
      LocalTime.MIDNIGHT,
      STOCKHOLM_TIME_ZONE,
    );
    return ok(new Date(zoned.toInstant().toEpochMilli()));
  } catch {
    return err("invalid-date");
  }
};

/** Format a LocalDate into a localized short weekday label. */
export const formatWeekdayShort = (date: LocalDate, locale: string): string => {
  const zoned = ZonedDateTime.of(date, LocalTime.MIDNIGHT, STOCKHOLM_TIME_ZONE);
  const jsDate = new Date(zoned.toInstant().toEpochMilli());
  const raw = new Intl.DateTimeFormat(locale, {
    weekday: "short",
    timeZone: STOCKHOLM_TIME_ZONE.id(),
  }).format(jsDate);
  return raw.length > 0 ? raw[0].toUpperCase() + raw.slice(1) : raw;
};

/** Format a LocalDate as day/month (e.g. 5/9). */
export const formatDayShort = (date: LocalDate): string =>
  `${date.dayOfMonth()}/${date.monthValue()}`;

export type TimeParts = {
  hours: number;
  minutes: number;
};

/** Compute shift duration in minutes, supporting overnight shifts. */
export const getShiftDurationMinutes = (
  start: TimeParts,
  end: TimeParts,
): number => {
  const startTotalMinutes = start.hours * 60 + start.minutes;
  const endTotalMinutes = end.hours * 60 + end.minutes;
  if (endTotalMinutes > startTotalMinutes) {
    return endTotalMinutes - startTotalMinutes;
  }
  return endTotalMinutes + 24 * 60 - startTotalMinutes;
};

/** Format a number as a 2-digit string. */
export const toTwoDigits = (value: number): string =>
  value.toString().padStart(2, "0");

/** Convert a date/time value into a HH:mm string or an error result. */
export const toTimeValue = (
  value?: string | null,
): Result<string, "invalid"> => {
  if (!value) return err("invalid");
  const zoned = toStockholmZonedDateTime(value);
  if (!zoned) return err("invalid");
  return ok(`${toTwoDigits(zoned.hour())}:${toTwoDigits(zoned.minute())}`);
};

/** Convert a date/time value into a Date or null if invalid. */
export const toDateValue = (value?: string | null): Date | null => {
  if (!value) return null;
  return toStockholmDate(value);
};

/** Parse a HH:mm time string into hour/minute parts. */
export const parseTimeValue = (value: string): TimeParts | null => {
  const match = /^(\d{1,2}):(\d{2})(?::\d{2})?$/.exec(value.trim());
  if (!match) return null;
  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  if (
    Number.isNaN(hours) ||
    Number.isNaN(minutes) ||
    hours < 0 ||
    hours > 23 ||
    minutes < 0 ||
    minutes > 59
  ) {
    return null;
  }
  return { hours, minutes };
};

/** Build a Date from a date and parsed time parts. */
export const buildDateWithTime = (date: Date, time: TimeParts): Date => {
  const localDate = LocalDate.ofInstant(
    Instant.ofEpochMilli(date.getTime()),
    STOCKHOLM_TIME_ZONE,
  );
  return buildDateWithTimeFromLocalDate(localDate, time);
};

/** Build a Date from a LocalDate and time parts in Stockholm time. */
export const buildDateWithTimeFromLocalDate = (
  date: LocalDate,
  time: TimeParts,
): Date => {
  const localTime = LocalTime.of(time.hours, time.minutes);
  const zoned = ZonedDateTime.of(date, localTime, STOCKHOLM_TIME_ZONE);
  return new Date(zoned.toInstant().toEpochMilli());
};

/** Add days in Stockholm time and return a Date for the resulting instant. */
export const addStockholmDays = (date: Date, days: number): Date => {
  const zoned = ZonedDateTime.ofInstant(
    Instant.ofEpochMilli(date.getTime()),
    STOCKHOLM_TIME_ZONE,
  );
  const next = zoned.plusDays(days);
  return new Date(next.toInstant().toEpochMilli());
};

/** Normalize a UTC ISO string by parsing and returning ISO (UTC). */
export const normalizeUtcIso = (
  value: string,
): Result<string, "invalid-date"> => {
  try {
    return ok(Instant.parse(value).toString());
  } catch {
    return err("invalid-date");
  }
};

/** Normalize a HH:mm string or fallback to a valid default. */
export const normalizeTimeValue = (value: string, fallback: string): string => {
  const parsed = parseTimeValue(value);
  if (parsed) {
    return `${toTwoDigits(parsed.hours)}:${toTwoDigits(parsed.minutes)}`;
  }
  const fallbackParsed = parseTimeValue(fallback);
  if (fallbackParsed) {
    return `${toTwoDigits(fallbackParsed.hours)}:${toTwoDigits(
      fallbackParsed.minutes,
    )}`;
  }
  return fallback;
};

/** Convert a break minutes input into a safe integer. */
export const toBreakMinutes = (value: string): number => {
  const parsed = Number(value);
  if (Number.isNaN(parsed) || parsed < 0) return 0;
  return Math.round(parsed);
};

/** Build start/end Date objects from a date and time range. */
export const buildShiftDates = (
  date: Date,
  startTime: string,
  endTime: string,
): Result<{ startDate: Date; endDate: Date }, "invalid-time"> => {
  const startParts = parseTimeValue(startTime);
  const endParts = parseTimeValue(endTime);
  if (!startParts || !endParts) {
    return err("invalid-time");
  }

  const startDate = buildDateWithTime(date, startParts);
  let endDate = buildDateWithTime(date, endParts);
  if (shouldRollToNextDay(startParts, endParts)) {
    endDate = addStockholmDays(endDate, 1);
  }
  return ok({ startDate, endDate });
};

/** Determine if an end time should roll into the next day. */
export const shouldRollToNextDay = (
  start: TimeParts,
  end: TimeParts,
): boolean => {
  if (end.hours > start.hours) return false;
  if (end.hours < start.hours) return true;
  return end.minutes < start.minutes;
};
